/*
  Miscreants product-page interactions.

  Wires per product section on the page:
    1. Sticky mobile ATC trigger forwarding clicks to the real Add to cart
    2. Accordion expand/collapse for product details rows
    3. Variant picker legend labels ("Size: M") + inline "Size chart" link
       that expands and scrolls to the size-chart accordion row
    4. Instant client-side patch of the Add to cart CTA + stock indicator
       on variant change — runs ahead of Dawn's product-info.js fetch so
       the button never flashes "disabled" during the network round-trip.
       Dawn's later fetch sets the same values; visually a no-op.

  Each <product-info id="MainProduct-…"> on the page is initialised once.
*/
(() => {
  document.querySelectorAll('[id^="MainProduct-"]').forEach(initProduct);
})();

// Accordions collapse to a single open panel below this width. Kept as a
// constant so it stays in step with Dawn's 749px mobile boundary.
const MS_ACCORDION_EXCLUSIVE_BELOW = '(max-width: 749px)';

// Single place that opens/closes a panel, so the click handler and the
// close-the-others loop can never drift apart on aria state.
function setAccordionState(trigger, open) {
  trigger.setAttribute('aria-expanded', String(open));
  const panelId = trigger.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : trigger.nextElementSibling;
  if (!panel) return;
  panel.classList.toggle('is-open', open);
  panel.setAttribute('aria-hidden', String(!open));
}

function initProduct(root) {
  const id = root.id.slice('MainProduct-'.length);

  const realButton = root.querySelector('[data-add-to-cart-button]');
  const stickyButton = root.querySelector('[data-mobile-atc-trigger]');
  if (realButton && stickyButton) {
    stickyButton.addEventListener('click', () => realButton.click());
  }

  root.querySelectorAll('[data-ms-accordion-trigger]').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const next = trigger.getAttribute('aria-expanded') !== 'true';

      // Mobile: one panel open at a time. Vertical space is scarce on a phone,
      // and with four panels open the CTA is pushed a long way off screen.
      // Desktop keeps multi-open — there is room, and comparing Specs against
      // Shipping side by side is useful there.
      //
      // matchMedia is read per click rather than cached, so the behaviour
      // follows a resize or an orientation change without a reload. 749px is
      // Dawn's mobile boundary, which the rest of the theme already uses.
      if (next && window.matchMedia(MS_ACCORDION_EXCLUSIVE_BELOW).matches) {
        const list = trigger.closest('.ms-accordion-list') || root;
        list.querySelectorAll('[data-ms-accordion-trigger]').forEach(other => {
          if (other !== trigger && other.getAttribute('aria-expanded') === 'true') {
            setAccordionState(other, false);
          }
        });
      }

      setAccordionState(trigger, next);
    });
  });

  const sizeChartTrigger = root.querySelector('[data-ms-accordion-trigger][data-source="size_chart"]');
  const updateLegends = () => {
    root.querySelectorAll('fieldset.product-form__input--pill').forEach(fs => {
      const legend = fs.querySelector('legend');
      if (!legend) return;
      const baseName = legend.dataset.msBaseName || legend.textContent.trim().replace(/:.*$/, '');
      legend.dataset.msBaseName = baseName;
      const isSizeOption = /size/i.test(baseName);
      const showSizeChartLink = isSizeOption && sizeChartTrigger;
      const checked = fs.querySelector('input[type="radio"]:checked');
      const linkHtml = showSizeChartLink
        ? '<a href="#" class="ms-variant-label__link" data-ms-size-chart-link>Size chart</a>'
        : '';
      legend.innerHTML = `<span class="ms-variant-label__main">${baseName}: <span class="ms-selected-value">${checked ? checked.value : ''}</span></span>${linkHtml}`;
    });
  };
  updateLegends();
  // Dawn's product-info.js replaces the entire <variant-selects> element on
  // variant change via HTMLUpdateUtility.viewTransition — our DOM mutations
  // get wiped. Re-apply after Dawn's swap, signalled by the variantChange
  // pubsub event.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      if (data?.sectionId === id) updateLegends();
    });
  }

  root.addEventListener('click', (e) => {
    const link = e.target.closest('[data-ms-size-chart-link]');
    if (!link || !sizeChartTrigger) return;
    e.preventDefault();
    if (sizeChartTrigger.getAttribute('aria-expanded') !== 'true') {
      sizeChartTrigger.click();
    }
    sizeChartTrigger.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  const variantData = (() => {
    const node = root.querySelector('product-model-data');
    if (!node) return [];
    try { return JSON.parse(node.textContent); } catch { return []; }
  })();
  const submitButton = root.querySelector(`#ProductSubmitButton-${id}`);
  const submitLabel = submitButton?.querySelector('.ms-btn__label');
  const inventoryNode = root.querySelector(`#Inventory-${id}`);
  const mobileButton = root.querySelector('[data-mobile-atc-trigger]');

  const findSelectedVariant = () => {
    const radios = root.querySelectorAll('variant-selects fieldset input[type="radio"]:checked');
    const selects = root.querySelectorAll('variant-selects select');
    const values = radios.length
      ? Array.from(radios).map(r => r.value)
      : Array.from(selects).map(s => s.value);
    return variantData.find(v =>
      (values[0] == null || v.option1 === values[0]) &&
      (values[1] == null || v.option2 === values[1]) &&
      (values[2] == null || v.option3 === values[2])
    );
  };

  const setBtn = (btn, label, available) => {
    if (!btn) return;
    const text = available
      ? (window.variantStrings?.addToCart || 'Add to cart')
      : (window.variantStrings?.soldOut || 'Sold out');
    if (available) btn.removeAttribute('disabled');
    else btn.setAttribute('disabled', 'disabled');
    if (label) label.textContent = text;
    else btn.textContent = text;
  };

  const applyVariant = (v) => {
    if (!v) return;
    setBtn(submitButton, submitLabel, !!v.available);
    setBtn(mobileButton, null, !!v.available);
    if (inventoryNode) {
      const dotClass = v.available ? 'ms-stock-line__dot--in' : 'ms-stock-line__dot--out';
      const text = v.available
        ? inventoryNode.dataset.inStockText
        : inventoryNode.dataset.soldOutText;
      inventoryNode.innerHTML =
        `<span class="ms-stock-line__dot ${dotClass}"></span><span>${text}</span>`;
    }
  };

  root.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="radio"], select')) return;
    const v = findSelectedVariant();
    applyVariant(v);
    // Instant legend "Size: M" before Dawn's fetch round-trip completes.
    // Dawn's variantChange pubsub will re-run updateLegends after the
    // fetch swaps in fresh HTML — idempotent.
    updateLegends();
    // Instant URL update — back-button + sharing stay in sync without
    // waiting for Dawn's fetch.
    if (v?.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState({}, '', url);
    }
  });
}
